<template>
  <div>
    <div v-if="direction === 'vertical'" class="game" :class="direction">
      <div
        :class="{ active: isMobile && active }"
        class="game-pic"
        @click.prevent="active = isMobile ? !active : false"
        @mouseleave="active = false"
      >
        <div class="position-absolute game-label-container">
          <div
            v-for="label in filteredLabels"
            :key="label"
            :class="{ 'd-none': !labels.includes(label) }"
            class="game-label"
          >
            <img
              :src="
                `https://ik.imagekit.io/1pzxddx3qqv/Z54IV3/static/${templateId}/game-label-${label}${
                  ['EXCLUSIVE', 'EARLY'].includes(label)
                    ? '-' + (['en', 'ja'].includes(locale) ? locale : 'en')
                    : ''
                }-v2.png`
              "
              :class="
                ['EXCLUSIVE', 'EARLY'].includes(label)
                  ? `game-label-${label}-${locale}`
                  : `game-label-${label}`
              "
              :alt="label"
            />
          </div>
        </div>
        <img :id="`img-${id}`" v-lazy="imgPath" :alt="name" />
        <div class="game-active">
          <button class="game-active-btn" @click.prevent="play(playParams)">
            {{ $t('common.play.now') }}
          </button>
          <a
            v-if="isShowTry"
            href="#"
            class="game-active-link"
            @click.prevent="playTry(playParams)"
          >
            {{ $t('common.play.playTry') }}
          </a>
        </div>
        <span
          :class="[
            favoriteState,
            {
              'event-none': favoriteState === 'activating'
            }
          ]"
          class="d-inline-block position-absolute game-favorite"
          @click.stop="toggleFavorite"
        ></span>
      </div>
      <h5 class="game-title">
        {{ name }}
      </h5>
    </div>
    <div
      v-if="direction === 'horizontal'"
      class="d-flex justify-content-between align-items-center game"
      :class="direction"
    >
      <div class="d-flex align-items-center">
        <div
          class="game-pic position-relative"
          style="width: 120px; height: 90.53px"
        >
          <!-- 2024/10/29 因新增tag(EARLY, NO-REBATE)-->
          <!-- 目前tag數量較多，設計決定先隱藏水平面的tag顯示(用於game search)  -->
          <!--          <div-->
          <!--            v-for="label in ['HOT', 'NEW', 'EXCLUSIVE']"-->
          <!--            :key="label"-->
          <!--            :class="{ 'd-none': !labels.includes(label) }"-->
          <!--            class="position-absolute game-label"-->
          <!--            :style="{-->
          <!--              width: labelImgWidth + 'px',-->
          <!--              height: labelImgHeight + 'px'-->
          <!--            }"-->
          <!--          >-->
          <!--            <img-->
          <!--              :src="-->
          <!--                `https://ik.imagekit.io/1pzxddx3qqv/Z54IV3/static/${templateId}/game-label-${label}${-->
          <!--                  label === 'EXCLUSIVE'-->
          <!--                    ? '-' + (['en', 'ja'].includes(locale) ? locale : 'en')-->
          <!--                    : ''-->
          <!--                }.png`-->
          <!--              "-->
          <!--              :alt="label"-->
          <!--            />-->
          <!--          </div>-->
          <img :id="`img-${id}`" v-lazy="imgPath" :alt="imgDescription" />
        </div>
        <h5 class="game-title">
          {{ name }}
        </h5>
      </div>
      <div class="d-flex flex-column justify-content-center game-active">
        <a
          v-if="isShowTry"
          href="#"
          class="game-active-link"
          @click.prevent="playTry(playParams)"
        >
          {{ $t('common.play.playTry') }}
        </a>
        <button class="game-active-btn" @click.prevent="play(playParams)">
          {{ $t('common.play.now') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  ref,
  toRefs,
  useStore,
  watch
} from '@nuxtjs/composition-api'
import { catchError, tap, from } from 'rxjs'

import usePlayGame from '../../composables/usePlayGame'
import useUser from '../../composables/useUser'
import useErrorHandler from '../../composables/useErrorHandler'
import useLocale from '../../composables/useLocale'
import useAuthModal from '../../composables/useAuthModal'

export default defineComponent({
  name: 'Game',
  props: {
    id: {
      type: String,
      required: true
    },
    imgPath: {
      type: String,
      default: ''
    },
    imgDescription: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    isShowTry: {
      type: Boolean,
      default: false
    },
    playParams: {
      type: Object,
      default() {
        return {}
      }
    },
    direction: {
      type: String,
      default: 'vertical' // vertical: 執事 / horizontal: 橫式
    },
    labels: {
      type: Array,
      default() {
        return []
      }
    },
    // labelImgWidth: {
    //   type: Number,
    //   default: 40
    // },
    // labelImgHeight: {
    //   type: Number,
    //   default: 26
    // },
    isFavorite: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const store = useStore()

    const { user } = useUser(context)
    const { locale } = useLocale()
    const { play, playTry } = usePlayGame(context)
    const { errorHandler$ } = useErrorHandler(context)
    const { linkToOpenAuthModal } = useAuthModal(context)

    const active = ref(false)

    const favoriteState = ref('inactive') // 'inactive' || 'activating' || 'active'

    const isMobile = computed(() => store.state.isMobile)
    const templateId = computed(() => process.env.TEMPLATE_ID)
    const filteredLabels = computed(() => {
      // 優先級規則
      const priorityLabels = ['EARLY', 'EXCLUSIVE', 'HOT', 'NEW']
      // 去除優先級後剩下的陣列值
      const additionalLabels = props.labels.filter(
        label => !priorityLabels.includes(label)
      )
      // EXCLUSIVE, HOT , NEW優先級防呆
      const firstPriorityLabel = priorityLabels.find(label =>
        props.labels.includes(label)
      )
      // 篩選後組成
      const displayLabels = firstPriorityLabel
        ? [firstPriorityLabel, ...additionalLabels]
        : additionalLabels
      // 鎖定顯示範圍
      const allowedLabels = [
        'EARLY',
        'HOT',
        'NEW',
        'EXCLUSIVE',
        'NO_BONUS',
        'NO_REBATE'
      ]
      return displayLabels.filter(label => allowedLabels.includes(label))
    })

    const { isFavorite, playParams } = toRefs(props)

    watch(isFavorite, () => refreshData())

    refreshData()

    function refreshData() {
      favoriteState.value = !isFavorite.value ? 'inactive' : 'active'
    }

    function toggleFavorite() {
      if (!user.value) {
        linkToOpenAuthModal('login')
        return
      }
      const beforeFavoriteState = favoriteState.value
      favoriteState.value = 'activating'
      from(
        store.dispatch(
          `gameModule/${
            beforeFavoriteState !== 'active' ? 'add' : 'remove'
          }GameFavorite`,
          playParams.value
        )
      )
        .pipe(
          tap(() => {
            favoriteState.value =
              beforeFavoriteState !== 'active' ? 'active' : 'inactive'
            context.emit('switchFavorite')
          }),
          catchError(err => {
            favoriteState.value = beforeFavoriteState
            return errorHandler$(err)
          })
        )
        .subscribe()
    }

    return {
      active,
      favoriteState,

      isMobile,
      templateId,
      filteredLabels,
      locale,

      play,
      playTry,
      toggleFavorite,
      linkToOpenAuthModal
    }
  }
})
</script>
